import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

import { AWS_ACCOUNT_PATTERN } from "./constants.ts";
import { arnInfo } from "./util.ts";

const OKTA_APP_PATTERN = /^0o\w+$/;
const AWS_IDP_PATTERN = /^[\w.\-/]+$/;
const AWS_CLOUDTRAIL_LAKE_PATTERN =
  /^arn:(aws|aws-us-gov|aws-cn|aws-iso|aws-iso-b):cloudtrail:.*:eventdatastore\/.*$/;

export const awsAccountIdValidator = async (_context: any, id: string) => {
  if (!id.match(AWS_ACCOUNT_PATTERN))
    return "AWS accounts should be 12 numeric digits";
};

const iamLoginOption = Option({
  maturity: "ga" as const,
  label: "As AWS IAM users",
  schema: {
    identity: Element({
      label: "How are users identified?",
      type: "select",
      default: "email",
      options: {
        email: Option({
          maturity: "ga" as const,
          label: "User name is user email",
          schema: {},
        }),
        tag: Option({
          maturity: "ga" as const,
          key: "tag" as const,
          label: "User email in a tag",
          schema: { tagName: Element({ type: "string", label: "Tag name" }) },
        }),
      },
    }),
  },
});

export const idcIdentityElement = Element({
  label: "How are users identified in AWS Identity Center?",
  type: "select",
  default: "user",
  options: {
    user: Option({
      maturity: "ga" as const,
      label: "Username is user's email",
      schema: {},
    }),
    email: Option({
      maturity: "ga" as const,
      label: "User's IDC email is user's email",
      schema: {},
    }),
  },
});

// Automatically-populated IDC metadata retrieved from the IDC API during installation.
// https://docs.aws.amazon.com/singlesignon/latest/APIReference/API_InstanceMetadata.html
export const idcMetadataElements = {
  idcArn: Element({
    type: "hidden",
    label: "The unique ARN of the Identity Center instance",
  }),
  identityStoreId: Element({
    type: "hidden",
    label:
      "ID of the identity store that is connected to the Identity Center instance",
  }),
} as const;

const idcLoginOption = Option({
  maturity: "ga" as const,
  label: "Via AWS Identity Center",
  description:
    "P0 must be installed in the account that contains the Identity Center instance.",
  schema: {
    parent: Element({
      label: "Account with Identity Center instance",
      type: "dynamic",
    }),
    identity: idcIdentityElement,
    ...idcMetadataElements,
    idcRegion: Element({
      type: "hidden",
      label: "Region the Identity Center instance is installed in",
    }),
  },
});

const mergedIdcLoginOption = Option({
  maturity: "beta" as const,
  label: "Via AWS Identity Center (merged permission set)",
  description:
    "Requires a separately installed AWS Identity Center (merged) integration. A single shared permission set per principal is used; each access request attaches its own customer-managed policy.",
  schema: {
    parent: Element({
      label: "Account with Identity Center instance",
      type: "dynamic",
    }),
  },
});

const federatedLoginOption = Option({
  maturity: "ga" as const,
  label: "Via a federated identity provider",
  schema: {
    provider: Element({
      label: "Identity provider",
      description: "Only Okta is supported at this time",
      type: "select",
      default: "okta",
      options: {
        okta: Option({
          maturity: "ga" as const,
          key: "okta",
          label: "Okta",
          schema: {
            appId: Element({
              label: "Federation app ID",
              description:
                "Application ID of your AWS federation application, in Okta.",
              type: "string",
              validator: async (_id, field) => {
                if (!field.match(OKTA_APP_PATTERN))
                  return "Okta app IDs should start with 0o and include no special characters";
              },
            }),
            identityProvider: Element({
              label: "Provider integration",
              description:
                "Identity of your identity provider integration, in AWS.",
              type: "string",
              validator: async (_id, field) => {
                if (!field.match(AWS_IDP_PATTERN))
                  return "AWS Identity Providers should contain no special characters other than /.";
              },
            }),
            method: Element({
              label: "SSO method",
              type: "select",
              default: "saml",
              options: {
                saml: {
                  label: "SAML",
                  maturity: "ga",
                  schema: {
                    accountCount: Element({
                      label: "Number of accounts linked to Federation app",
                      type: "select",
                      default: "single",
                      options: {
                        single: {
                          label: "Single account",
                          maturity: "ga",
                          schema: {},
                        },
                        multi: {
                          label: "Multiple accounts (legacy)",
                          maturity: "ga",
                          description:
                            'Choose if you have multiple "Connected Accounts IDs" on the Provisioning / Integration tab in your Federation app',
                          schema: {
                            parent: Element({
                              label: "Parent AWS account",
                              type: "string",
                              validator: awsAccountIdValidator,
                            }),
                          },
                        },
                      },
                    }),
                  },
                },
              },
            }),
          },
        }),
        "google-workspace": Option({
          maturity: "ga" as const,
          label: "Google Workspace",
          hidden: true,
          description: "Google Workspace",
          type: "object",
          schema: {},
        }),
      },
    }),
  },
});

export const loginOptions = {
  iam: iamLoginOption,
  idc: idcLoginOption,
  "merged-idc": mergedIdcLoginOption,
  federated: federatedLoginOption,
};

export const AwsPartitionElement = Element({
  label: "Which AWS partition is this account in?",
  description:
    'Read the partition from any IAM role, policy, or resource ARN. The second item in every ARN is the partition: "arn:<partition>:<service>:<region>:<account-id>:<resource>"',
  type: "select",
  default: "aws",
  options: {
    aws: { label: "Commercial (aws)", maturity: "ga", schema: {} },
    "aws-us-gov": {
      label: "GovCloud (aws-us-gov)",
      maturity: "beta",
      schema: {},
    },
  },
  step: "new",
});

export const AwsComponents = InstallSpec({
  /** Base is a generated component. It carries P0's service-account integration data.
   *
   * This is not created directly by the user; rather, the user-initiated component
   * assemblers should assemble this component implicitly.
   */
  base: ItemComponent({
    label: "Base installation",
    description: "Defines how P0 connects to your AWS account",
    type: "generated",
    hidden: true,
    maturity: "ga",
    schema: {
      serviceAccountEmail: Element({
        label: "P0 service account human-readable identifier",
        type: "generated",
      }),
      serviceAccountId: Element({
        label: "P0 service account machine identifier",
        type: "generated",
      }),
    },
  }),
  "iam-assessment": ItemComponent({
    label: "IAM assessment",
    description:
      "Allows P0 to analyze your IAM configuration for security issues.  Required for IAM Posture and Access Inventory.",
    type: "string",
    maturity: "ga",
    schema: {
      awsPartition: AwsPartitionElement,
      accessAnalyzerArn: Element({
        label: "ARN of this account's IAM Access Analyzer",
        type: "string",
        validator: async (id: string, arn: string) => {
          if (!arn) {
            return "Access Analyzer ARN is required";
          }

          try {
            const arnAccountId = arnInfo(arn, true).accountId;

            if (!arnAccountId) {
              return "Access Analyzer ARN must include an account ID";
            }

            if (arnAccountId !== id) {
              return `Access Analyzer ARN account mismatch: ARN is for account ${arnAccountId} but configuring account ${id}. Please use an Access Analyzer in the same account.`;
            }
          } catch (error) {
            return `Invalid Access Analyzer ARN: ${error instanceof Error ? error.message : String(error)}`;
          }
        },
      }),
    },
    validator: awsAccountIdValidator,
  }),
  "iam-write": ItemComponent({
    label: "IAM management",
    description:
      "Allows P0 to grant and revoke AWS access for your users. Required for just-in-time access.",
    type: "string",
    maturity: "ga",
    schema: {
      awsPartition: AwsPartitionElement,
      login: Element({
        label: "How are users provisioned?",
        type: "select" as const,
        default: "iam",
        options: loginOptions,
      }),
    },
    validator: awsAccountIdValidator,
  }),
  "function-caller": ItemComponent({
    label: "Function invocation",
    description:
      'Allows P0 to invoke AWS Lambda functions in your AWS account. Configure your AWS Lambda as a webhook in other integrations such as the "AWS Lambda" notifier integration.',
    type: "string",
    maturity: "ga",
    schema: {
      accountId: Element({
        label: "AWS account ID",
        type: "hidden",
        validator: awsAccountIdValidator,
      }),
      awsPartition: Element({
        label: "This account's AWS partition",
        type: "hidden",
      }),
    },
    validator: async (_id, field) => {
      try {
        const arn = arnInfo(field);
        if (!arn.accountId) return "Lambda ARN must include an account ID";
        if (!arn.name) return "Lambda ARN must include a function name";
      } catch {
        return "Invalid Lambda ARN";
      }
    },
  }),
  "rds-security-perimeter": ItemComponent({
    label: "RDS security perimeter",
    description:
      "Allows P0 to install a Security Perimeter into your AWS VPC. This is required for just-in-time access to RDS databases.",
    type: "string",
    maturity: "ga",
    schema: {
      accountId: Element({
        label: "AWS account ID",
        type: "dynamic",
        step: "new",
      }),
      vpcId: Element({
        label: "VPC ID",
        description: "The AWS VPC in which to install the security perimeter.",
        type: "string",
        step: "new",
      }),
      region: Element({
        label: "Region",
        description:
          "The AWS region in which to install the security perimeter.",
        type: "string",
        step: "new",
      }),
      subnetIds: Element({
        label: "Subnet IDs",
        description:
          "The AWS subnets in the VPC. These must be comma delimited.",
        type: "string",
        step: "new",
      }),
    },
  }),
  "access-logs": ItemComponent({
    label: "Access logging",
    description:
      "Allows P0 to query CloudTrail logs in your AWS account. NOTE: This will incur additional costs upon usage. Please contact P0 support or refer to https://aws.amazon.com/cloudtrail/pricing/ for more information.",
    type: "dynamic",
    maturity: "ga",
    schema: {
      cloudLakeArn: Element({
        step: "new",
        label: "What is the ARN of your CloudLake data store?",
        type: "string",
        validator: async (_id, field) => {
          if (!field.match(AWS_CLOUDTRAIL_LAKE_PATTERN))
            return "CloudLake ARNs should begin with arn:aws:cloudtrail: and include a region, account ID, and event datastore ID";
        },
      }),
    },
  }),
  inventory: ItemComponent({
    label: "Resource inventory",
    description:
      "Allows P0 to inventory your AWS resources. Required for resource-level just-in-time access.",
    type: "dynamic",
    maturity: "ga",
    schema: {},
  }),
});
